import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridSortModel} from '@mui/x-data-grid';
import {GridRenderHeaderFilterProps} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import {difference, intersection, isEmpty, uniq, uniqBy} from 'lodash';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  downloadCustodiansCsv,
  getCustodianEca,
} from '../../../api/EcaManagementService';
import {ReactComponent as DownloadIcon} from '../../../assests/svg/download-icon.svg';
import {
  SOURCE_TYPE,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import {RootState} from '../../../redux/store';
import {
  downloadResponseAsCSV,
  getProjectReportFileName,
} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridPro from '../../common/aur-data-grid-pro/AurDataGridPro';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPagination from '../../common/aur-pagination/AurPagination'; // Import AurPagination
import BarChartCell from '../../reports/HostingVolumeReport/BarChartCell';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

interface CustodianDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null | undefined;
  loadFileId?: number;
  projectName?: string | null | undefined;
}
interface CustodianRow {
  custodianId?: string;
  name: string;
  documents?: number;
  documentCount: number;
}
interface CustodianData {
  custodian: CustodianRow[];
  totalDocumentsCount: number;
  uniqueCustodians: number;
}

const CustodianDialog: React.FC<CustodianDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  loadFileId,
  projectName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {trackGAEvent} = useAnalytics();

  const [loading, setLoading] = useState(false);
  const [selectedCustodians, setSelectedCustodians] = useState<CustodianRow[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'documentcount',
      sort: 'desc',
    },
  ]);
  const [custodianData, setCustodianData] = useState<CustodianData | null>(
    null
  );
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [custodianRows, setCustodianRows] = useState<CustodianRow[]>([]);
  const [totalItems, setTotalItems] = useState<number>();

  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);
  useEffect(() => {
    if (isOpen) {
      setItemsPerPage(TABLE_PER_PAGE_DEFAULT_NO_ROW);
      setCurrentPage(1);
    }
  }, [isOpen]);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const fetchCustodianTally = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}`;

      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }

      if (sortModel[0]?.field) {
        paramString += `&SortColumn=${sortModel[0].field}`;
      }

      if (sortModel[0]?.sort) {
        paramString += `&SortOrder=${sortModel[0].sort}`;
      }

      paramString += `&PageNumber=${currentPage}&PageSize=${itemsPerPage}`;

      const response = await getCustodianEca(paramString);
      const res = response.data;
      const data = res?.data || [];
      const custodianArray = data?.custodian ?? [];

      setTotalItems(res?.meta?.pagination?.totalItems ?? 0);
      setCustodianData({
        ...(data ?? {}),
        custodian: [...custodianArray],
      });
      setCustodianRows([...custodianArray]);
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCustodianTally();
    }
  }, [isOpen, currentPage, itemsPerPage, sortModel]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCustodians([]);
    }
  }, [isOpen]);

  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  useEffect(() => {
    if (isOpen) {
      handleDownloadCustodiansCsv();
    }
  }, []);

  const handleDownloadCustodiansCsv = async () => {
    setIsClearAllLoading(true);

    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}`;
      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }
      if (sortModel?.[0]?.field) {
        paramString += `&SortColumn=${sortModel[0].field}`;
      }

      if (sortModel?.[0]?.sort) {
        paramString += `&SortOrder=${sortModel[0].sort}`;
      }

      paramString += `&PageNumber=${1}&PageSize=${totalItems}`;

      const response = await downloadCustodiansCsv(paramString);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(
          projectName,
          t('ecaHomepageModals.ecaBottom.allProcessedCustodians')
        )
      );
    } catch (e) {
      console.log(e);
    } finally {
      setIsClearAllLoading(false);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.CSV,
        click: {
          name: t('gaTracking.global.downloadReport'),
          region: GA_REGIONS.MODAL,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.global.downloadedReport'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
            label: t('gaTracking.ecaHomePage.allProcessedCustodians'),
          }),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  const columns = [
    ...(hasSearchesPerm
      ? [
          {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            headerName: '',
            renderHeader: () => <span className="sr-only">{t('common.checkboxColumnHeader')}</span>,
            renderHeaderFilter: () => <span className="sr-only">{t('common.checkboxColumnFilter')}</span>,
            isBorderNotRequired: true,
          },
        ]
      : []),
    {
      field: 'name',
      headerName: t('ecaHomepageModals.custodianModal.custodianColumn'),
      width: 150,
      sortable: false,
      flex: 0.5,
      isBorderNotRequired: true,
      headerClassName: 'no-separator',
      headerFilters: true,
      renderCell: (params: any) => (
        <AurDataGridCellWrapper
          cellData={params.value}
          ellipsisDisabled={true}
          tooltipDisabled={true}
        />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
        return (
          <Box className="header-filter-grid d-flex" gap={1}>
            <FilterTextField
              gridRenderHeaderFilterProps={params}
              variant="outlined"
              size="small"
              placeholder={t('common.filters.filterLable')}
              className="bgColor-change"
            />
          </Box>
        );
      },
    },
    {
      field: 'documentcount',
      headerName: t('ecaHomepageModals.custodianModal.custodianDocColumn'),
      minWidth: 200,
      sortable: true,
      flex: 1.5,
      headerClassName: 'no-separator',
      renderHeaderFilter: () => <span className="sr-only">{t('ecaHomepageModals.custodianModal.custodianDocColumn')}</span>,
      renderCell: (params: any) => (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <BarChartCell
            value={params.row.documentCount}
            maxValue={Math.max(
              ...custodianRows.map((row: any) => row.documentCount)
            )}
            name="custodian"
            showLabel={true}
            fullWidthBar={true}
            showTooltip={false}
            theme="light"
          />
        </Box>
      ),
    },
  ];

  const handleSelectionChange = (newSelection: (number | string)[]) => {
    const currentPageRowIdArray = custodianRows.map((item) => getRowId(item));
    const selectedCustodianRowIdArray = selectedCustodians.map((item) =>
      getRowId(item)
    );
    const currentPageSelectedRowIdArray = intersection(
      selectedCustodianRowIdArray,
      currentPageRowIdArray
    );

    let updatedSelectedCustodians: CustodianRow[] = [];
    let updatedSelectedRows: string[] = [];

    const currentPageSelectedCustodians: CustodianRow[] = custodianRows.filter(
      (item) => newSelection.includes(getRowId(item))
    );

    if (isEmpty(currentPageSelectedRowIdArray)) {
      updatedSelectedCustodians = [
        ...selectedCustodians,
        ...currentPageSelectedCustodians,
      ];
    } else {
      updatedSelectedRows = difference(
        selectedCustodianRowIdArray,
        currentPageSelectedRowIdArray
      );
      updatedSelectedRows = [
        ...updatedSelectedRows,
        ...(newSelection as string[]),
      ];
      updatedSelectedRows = uniq(updatedSelectedRows);

      updatedSelectedCustodians = [
        ...selectedCustodians,
        ...custodianRows,
      ].filter((item) => updatedSelectedRows.includes(getRowId(item)));
    }

    updatedSelectedCustodians = uniqBy(updatedSelectedCustodians, 'name');
    setSelectedCustodians(updatedSelectedCustodians);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const renderModalHeader = () => (
    <Typography className="typo-text headline3_size" id="custodian-dialog-title">
      {t('ecaHomepageModals.ecaBottom.allProcessedCustodians')}
    </Typography>
  );

  const renderHeaderActions = () => (
    <Box className="header-alignment">
      <Box display={'flex'} gap={3}>
        <Box className="modal-count-header">
          <Typography className="typo-text display3_size">
            {custodianData?.uniqueCustodians}
          </Typography>
          <Typography className="typo-text subhead4_size">
            {t('ecaHomepageModals.custodianModal.uniqueCustodians')}
          </Typography>
        </Box>
        <Box className="modal-count-header">
          <Typography className="typo-text display3_size">
            {custodianData?.totalDocumentsCount}
          </Typography>
          <Typography className="typo-text subhead4_size">
            {t('ecaHomepageModals.custodianModal.totalDocument')}
          </Typography>
        </Box>
      </Box>
      <Box>
        <AurButton
          data-testid="export-button"
          onClick={handleDownloadCustodiansCsv}
          startIcon={
            isClearAllLoading ? (
              <div className="color-loader">
                <AurLoadingIndicator
                  size={16}
                  className="progress-icon"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <DownloadIcon aria-hidden="true" />
            )
          }
          type="continue"
          size="Medium"
          disabled={isClearAllLoading}
          label={t('common.downloadReport')}
          classNames="typo-text buttonMedium_size"
        />
      </Box>
    </Box>
  );

  const getRowId = (row: CustodianRow) => row?.name ?? '';

  const renderTable = () => {
    if (!custodianRows || !custodianRows.length) return null;

    return (
      <div className="table-data space-top-30">
        <AurDataGridPro
          className="project-list-grid scroll-container-list"
          rows={custodianRows}
          headerFilters
          columns={columns}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          sortModel={sortModel}
          setSortModel={onSetSortModel}
          showPagination={false}
          noRowsOverlay={
            isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
          }
          defaultItemsPerPage={itemsPerPage}
          defaultSelectedtPage={currentPage}
          totalRowCount={custodianRows?.length ?? 0}
          paginationModel={{page: currentPage - 1, pageSize: itemsPerPage}}
          sortingMode="server"
          paginationMode="server"
          disableRowSelectionOnClick={!hasSearchesPerm}
          checkBoxVisible={hasSearchesPerm}
          addedInDialog
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          classNameOfParentScollElement='table-data'
        />
      </div>
    );
  };

  const renderDialogActions = () => (
    <DialogActions className="footer-action-container remove-pagination-status">
      <AurPagination
        totalItems={totalItems || 0}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        perPageOptions={TABLE_PAGE_SIZE_OPTION}
        setItemsPerPage={handlePageSizeChange}
        onPageChange={handlePageChange}
      />
      <div className="footer-block">
        <Stack>
          <AurButton
            onClick={onClose}
            type="exit"
            size="Medium"
            label={t('ecaHomepageModals.docTypeModal.closeText')}
            classNames="pagination-cancel-button"
            dataTestId="pagination-cancel-button"
          />
        </Stack>
        {hasSearchesPerm && (
          <Stack>
            <AurButton
              onClick={() => {
                navigate('/index-search', {
                  state: {
                    source: SOURCE_TYPE.custodian,
                    selected: selectedCustodians,
                  },
                });
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.SEARCH,
                  click: {
                    name: t('gaTracking.ecaHomePage.ViewInDoc'),
                    region: GA_REGIONS.MODAL,
                    type: GA_CLICK_TYPES.BUTTON,
                    action: t('gaTracking.ecaHomePage.searchDocuments'),
                  },
                  subPage: {
                    subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                      label: t('gaTracking.ecaHomePage.allProcessedCustodians'),
                    }),
                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                  },
                });
              }}
              type="continue"
              size="Medium"
              label={t('ecaHomepageModals.docTypeModal.ViewInDoc')}
              disabled={isEmpty(selectedCustodians)}
              classNames="pagination-continue-button"
              dataTestId="pagination-continue-button"
            />
          </Stack>
        )}
      </div>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      PaperProps={{className: 'evidence-source-dialog'}}
      maxWidth={false}
      aria-labelledby='custodian-dialog-title'
    >
      <DialogContent className="model-content">
        {renderModalHeader()}
        {renderHeaderActions()}
        {renderTable()}
      </DialogContent>
      {renderDialogActions()}
      <AurLoadingIndicator overlay={true} isOpen={loading} />
    </Dialog>
  );
};

export default CustodianDialog;
